import { useState } from 'react';
import { Select, SelectOption, Text, TextField } from '@recargas-dominicanas/core/components';
import type { IBusiness, ICustomer, Nullable } from '@recargas-dominicanas/core/types';
import { CustomerApi } from '@recargas-dominicanas/core/adminApi';
import { BusinessStore } from '../../../store/businessStore';
import { Modal } from '../../../components/modal/modal.component';
import { useForm } from '@recargas-dominicanas/core/hooks';
import { style } from './edit-customer-modal.module.css';
import { emptyToUndefine } from '../../../utils/emptyToUndefine';

interface Props { 
  open: boolean;
  business: IBusiness;
  onClose: () => void;
}

export function EditCustomerModal(props: Props) {

  const { open, business, onClose } = props;

  const [error, setError] = useState<string>();

  const form = useForm({
    name: business.customer.name,
    docNumber: { 
      value: business.customer.docNumber ?? '', 
      optional: true,
      minLength: { length: 11, error: 'Cédula invalida' }
    },
    phone: { value: business.customer.phone ?? '', phone: true },
    email: { value: business.customer.email ?? '', optional: true, email: true },
    status: business.customer.status as Nullable<ICustomer['status']>,
    businessName: business.name,
    businessRnc: { value: business.rnc ?? '', optional: true },
    businessPhone: { value: business.phone ?? '', optional: true, phone: true },
    businessEmail: { value: business.email ?? '', optional: true, email: true },
    businessCity: business.city,
    businessAddress: business.address,
    system: business.system as Nullable<IBusiness['system']>,
    isDistributor: business.customer.isDistributor ? 'yes' : 'no'
  }, [open]);

  function handleClose() {
    setError(undefined);
    form.clear();
    onClose();
  }

  async function handleSave() {

    if (!form.validate()) return;

    setError(undefined);
    
    try {

      await CustomerApi.update(business.customerId, {
        businessId: business.id,
        name: form.name.value,
        docNumber: emptyToUndefine(form.docNumber.value),
        phone: emptyToUndefine(form.phone.value),
        email: emptyToUndefine(form.email.value),
        status: form.status.value,
        businessName: form.businessName.value,
        businessRnc: emptyToUndefine(form.businessRnc.value),
        businessPhone: emptyToUndefine(form.businessPhone.value),
        businessEmail: emptyToUndefine(form.businessEmail.value),
        businessCity: form.businessCity.value,
        businessAddress: form.businessAddress.value,
        system: form.system.value,
        isDistributor: form.isDistributor.value === 'yes'
      });

      await BusinessStore.fetchById(business.id);
      
      handleClose();
    
    } catch {

      setError('Ocurrio un error al actualizar los datos del cliente');
    }
  }

  return (
    <Modal
      open={open}
      icon='person'
      title='Editar cliente'
      primaryButton='Guardar'
      error={error}
      style={style.modal}
      onPrimaryClick={handleSave}
      onClose={handleClose}
      onSecondaryClick={handleClose}>
      <Text 
        className={style.customerTitle} 
        text='Datos del cliente'/>
      <div className={style.fieldsContent}>
        <TextField 
          style={style.textField} 
          label='Nombre'
          value={form.name.value}
          error={form.name.error}
          onChange={form.name.onChange}
          onBlur={form.name.onBlur}/>
        <TextField 
          style={style.textField} 
          label='Cédula (opcional)'
          value={form.docNumber.value}
          error={form.docNumber.error}
          onChange={form.docNumber.onChange}
          onBlur={form.docNumber.onBlur}/>
        <TextField 
          style={style.textField} 
          label='Teléfono'
          value={form.phone.value}
          error={form.phone.error}
          onChange={form.phone.onChange}
          onBlur={form.phone.onBlur}/>
        <TextField 
          style={style.textField} 
          label='Correo electrónico (opcional)'
          value={form.email.value}
          error={form.email.error}
          onChange={form.email.onChange}
          onBlur={form.email.onBlur}/> 
        <Select 
          style={style.select} 
          label='Estado'
          placeholder='Seleccionar estado'
          value={form.status.value}
          error={form.status.error}
          onChange={(value) => {
            form.status.onChange(value);
            form.status.validate(value);
          }}>
          <SelectOption value='new' label='Nuevo'>Nuevo</SelectOption>
          <SelectOption value='active' label='Activo'>Activo</SelectOption>
          <SelectOption value='inactive' label='Inactivo'>Inactivo</SelectOption>
          <SelectOption value='lost' label='Perdido'>Perdido</SelectOption>
        </Select>
      </div>
      <Text 
        className={style.businessTitle} 
        text='Datos del Negocio'/>
      <div className={style.fieldsContent}>
        <TextField 
          style={style.textField} 
          label='Nombre'
          value={form.businessName.value}
          error={form.businessName.error}
          onChange={form.businessName.onChange}
          onBlur={form.businessName.onBlur}/>
        <TextField 
          style={style.textField} 
          label='RNC (opcional)'
          value={form.businessRnc.value}
          error={form.businessRnc.error}
          onChange={form.businessRnc.onChange}
          onBlur={form.businessRnc.onBlur}/>
        <TextField 
          style={style.textField} 
          label='Teléfono (opcional)'
          value={form.businessPhone.value}
          error={form.businessPhone.error}
          onChange={form.businessPhone.onChange}
          onBlur={form.businessPhone.onBlur}/>
        <TextField 
          style={style.textField} 
          label='Correo electrónico (opcional)'
          value={form.businessEmail.value}
          error={form.businessEmail.error}
          onChange={form.businessEmail.onChange}
          onBlur={form.businessEmail.onBlur}/> 
        <TextField 
          style={style.textField} 
          label='Ciudad'
          value={form.businessCity.value}
          error={form.businessCity.error}
          onChange={form.businessCity.onChange}
          onBlur={form.businessCity.onBlur}/>
        <TextField 
          style={style.textField} 
          label='Dirección'
          value={form.businessAddress.value}
          error={form.businessAddress.error}
          onChange={form.businessAddress.onChange}
          onBlur={form.businessAddress.onBlur}/>
        <Select 
          style={style.select} 
          label='Sistema'
          placeholder='Seleccionar sistema'
          value={form.system.value}
          error={form.system.error}
          onChange={(value) => {
            form.system.onChange(value);
            form.system.validate(value);
          }}>
          <SelectOption value='mobile-app' label='Aplicación móvil'>Aplicación móvil</SelectOption>
          <SelectOption value='webapp' label='Aplicación web'>Aplicación web</SelectOption>
          <SelectOption value='unknown' label='Ninguno'>Ninguno</SelectOption>
        </Select>
        <Select 
          style={style.select} 
          label='Es distribuidor?'
          placeholder='Seleccionar'
          value={form.isDistributor.value}
          error={form.isDistributor.error}
          onChange={(value) => {
            form.isDistributor.onChange(value ?? 'no');
            form.isDistributor.validate(value ?? 'no');
          }}>
          <SelectOption value='yes' label='Sí'>Sí</SelectOption>
          <SelectOption value='no' label='No'>No</SelectOption>
        </Select>
      </div>
    </Modal>
  );
}
